import React, { useState, useEffect } from 'react';
import { 
  useGetCrawledCuponsQuery, 
} from 'state/api';
import TailwindDataTable from 'scenes/crawled_cupons/components/TailwindDataTable';
import Snackbar from 'components/Snackbar';
import AppBarWithFilters from '../components/AppBar';
import SyncTrButton from '../components/SyncTrButton';
import getColumns from './columns';
import useFilteredData from '../components/FilteredData';
import useFilters from '../components/useFilters';
import useRunCancelCommand from '../components/useRunCancelCommand';
import ExportCouponsModal from '../components/ExportCouponsModal';
import TRActionModal from '../components/TRActionModal';
import { exportToCSV } from '../hooks/exportToCSV';

const CrawledCouponsList = ({ spider }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(500);
  const [selectedSpider, setSelectedSpider] = useState("cuponomia");
  const { data = [], isLoading, refetch } = useGetCrawledCuponsQuery({}, { skip: false });
  const [spiderOptions, setSpiderOptions] = useState([]);
  const [isFilterOn] = useState(true);
  const { filters, handleFiltersChange } = useFilters();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isTRModalOpen, setIsTRModalOpen] = useState(false);
  const [trAction, setTrAction] = useState(null); // 'add' ou 'remove'
  
  const { 
    isRunning, 
    snackbarMessage, 
    openSnackbar, 
    handleRunCommand, 
    handleCancel, 
    setOpenSnackbar,
    setSnackbarMessage // Pode não estar disponível no hook
  } = useRunCancelCommand();

  // Função para definir mensagem do snackbar (fallback se não vier do hook)
  const showSnackbar = (message) => {
    if (setSnackbarMessage) {
      setSnackbarMessage(message);
    } else {
      // Se setSnackbarMessage não estiver disponível, use um estado local
      console.log('Snackbar message:', message);
    }
    setOpenSnackbar(true);
  };

  const availableFields = [
    { name: 'title', label: 'Título' },
    { name: 'description', label: 'Descrição' },
    { name: 'code', label: 'Código' },
    { name: 'url', label: 'URL' },
    { name: 'store_url', label: 'URL da Loja' },
    { name: 'store_name', label: 'Nome da Loja' },
    { name: 'spider', label: 'Spider' },
    { name: 'store_slug', label: 'SLUG' },
    { name: 'is_on_tr', label: 'TR?' },
    { name: 'last_time_seen', label: 'Visto por último' },
    { name: 'first_time_seen', label: 'Visto primeira vez' },
  ];
  const defaultSelectedFields = ['title', 'description', 'code', 'url'];

  const handleExportModal = () => {
    setIsExportModalOpen(true);
  };

  // Função para lidar com a exportação do modal
  const handleModalExport = (exportedData, selectedFields) => {
    console.log('Dados exportados:', exportedData);
    console.log('Campos selecionados:', selectedFields);
    
    showSnackbar(`Exportação concluída! ${selectedFields.length} campos exportados.`);
  };

  // Função para exportação rápida (nova)
  const handleExport = () => {
    const selectedData = filteredData.filter((row) => selectedRows.includes(row.id));
    const columns = getColumns(isFilterOn, filters, handleFiltersChange, spiderOptions, selectedSpider, handleSpiderChange);
    exportToCSV(columns, selectedData.length > 0 ? selectedData : filteredData);
    
    showSnackbar(`${selectedData.length > 0 ? selectedData.length : filteredData.length} registros exportados com sucesso!`);
  };

  // Função para abrir modal de ações TR
  const handleTRAction = (action) => {
    if (selectedRows.length === 0) {
      showSnackbar('Selecione pelo menos um cupom para realizar esta ação.');
      return;
    }
    
    setTrAction(action);
    setIsTRModalOpen(true);
  };

  // Função para confirmar ação TR
  const handleTRConfirm = async (selectedData, action) => {
    try {
      console.log(`Ação ${action} para ${selectedData.length} cupons:`, selectedData);
      
      // Simulação de requisição (seu colega já deve ter implementado a real)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Recarregar dados após ação
      refetch();
      
      const actionText = action === 'add' ? 'adicionados ao' : 'removidos do';
      showSnackbar(`${selectedData.length} cupom(s) ${actionText} TR com sucesso!`);
      
      // Limpar seleção
      setSelectedRows([]);
      
    } catch (error) {
      console.error('Erro na ação TR:', error);
      showSnackbar('Erro ao processar ação. Tente novamente.');
    }
  };

  const ALL_SPIDERS = [
    "agoracupom",
    "ciadosdescontos",
    "cuponeria",
    "cuponomia",
    "descontocupom",
    "efacil_cupons",
    "ganhecupons",
    "kabum_cupons",
    "pegadesconto",
    "pelandocupons",
    "promobitcupons",
    "terabyteshop_cupons",
    "todoscupons"
  ];

  useEffect(() => {
    if (Array.isArray(data)) {
      const uniqueSpiders = Array.from(new Set([
        ...ALL_SPIDERS,
        ...data.map(item => item.spider)
      ])).filter(Boolean).sort();
      setSpiderOptions(uniqueSpiders);
    } else {
      setSpiderOptions(ALL_SPIDERS);
    }
  }, [data]);

  const filteredData = useFilteredData(data, selectedSpider, filters);

  const handleSpiderChange = (event) => {
    setSelectedSpider(event.target.value);
  };  

  const handleSyncComplete = (syncData) => {
    console.log('Sincronização concluída:', syncData);
  };

  const columns = getColumns(isFilterOn, filters, handleFiltersChange, spiderOptions, selectedSpider, handleSpiderChange);

  return (
    <div className="flex flex-col space-y-4 p-4 min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Cabeçalho com gradiente de cores */}
      <div className="rounded-lg bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 p-6 shadow-lg">
        <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
          Cupons Capturados
        </h1>
        <p className="text-blue-100">
          Gerencie todos os cupons capturados pelos crawlers
        </p>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-blue-500">
            <p className="text-sm font-medium text-gray-500">Total de Cupons</p>
            <p className="text-2xl font-bold text-blue-600">{data.length}</p>
          </div>
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-purple-500">
            <p className="text-sm font-medium text-gray-500">Cupons Filtrados</p>
            <p className="text-2xl font-bold text-purple-600">{filteredData.length}</p>
          </div>
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-green-500">
            <p className="text-sm font-medium text-gray-500">Spiders Disponíveis</p>
            <p className="text-2xl font-bold text-green-600">{spiderOptions.length}</p>
          </div>
          <div className="bg-white/90 backdrop-blur rounded-lg p-4 shadow-md border-l-4 border-red-500">
            <p className="text-sm font-medium text-gray-500">Selecionados</p>
            <p className="text-2xl font-bold text-red-600">{selectedRows.length}</p>
          </div>
        </div>
      </div>
  
      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
        severity="warning"
      />
  
      {/* AppBar estilizado com container */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2"></div>
        <AppBarWithFilters
          selectedSpider={selectedSpider}
          spiderOptions={spiderOptions}
          handleSpiderChange={handleSpiderChange}
          handleExport={handleExport}
          handleExportModal={handleExportModal}
          handleTRAction={handleTRAction}
          selectedRowsCount={selectedRows.length}
          handleRunCommand={handleRunCommand}
          isRunning={isRunning}
          handleCancel={handleCancel}
          showRunCommand={true}
          showAdvancedExport={true}
          showTRActions={true}
        >
          <SyncTrButton onSyncComplete={handleSyncComplete} />
        </AppBarWithFilters>
      </div>
  
      {/* Tabela com container */}
      <div className="bg-white rounded-lg shadow-md overflow-visible">
        <div className="bg-gradient-to-r from-pink-500 to-orange-500 h-2"></div>
        <TailwindDataTable
          data={filteredData}
          isLoading={isLoading}
          columns={columns}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setSelectedRows={setSelectedRows}
          getRowId={(row) => row.id}
        />
      </div>
      
      {/* Rodapé - agora com padding-top para dar espaço à paginação */}
      <div className="text-center p-4 pt-12 text-sm text-gray-500">
        <p>Última atualização: {new Date().toLocaleString()}</p>
      </div>

      {/* Modais */}
      <ExportCouponsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        data={selectedRows.length > 0 ? filteredData.filter(row => selectedRows.includes(row.id)) : filteredData}
        availableFields={availableFields}
        defaultSelectedFields={defaultSelectedFields}
        onExport={handleModalExport}
      />

      <TRActionModal
        isOpen={isTRModalOpen}
        onClose={() => setIsTRModalOpen(false)}
        selectedData={filteredData.filter(row => selectedRows.includes(row.id))}
        action={trAction}
        onConfirm={handleTRConfirm}
      />
    </div>
  );
};

export default CrawledCouponsList;